import React, { useState, useEffect } from "react";
import { supabase } from "../supabaseClient";
import { useNavigate } from "react-router-dom";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false); // Used for auth and redirecting
  const [initialCheckDone, setInitialCheckDone] = useState(false); // UX smoother
  const navigate = useNavigate();

  // ---------------------------------------------------------
  // 1. REUSABLE REDIRECT LOGIC
  // ---------------------------------------------------------
  const handleRoleRedirect = async (userId) => {
    try {
      // Fetch Role
      const { data: userData, error: roleError } = await supabase
        .from("users")
        .select("role")
        .eq("id", userId)
        .single();

      if (roleError) throw roleError;

      // Route based on Role
      switch (userData.role) {
        case "Athlete":
          navigate("/");
          break;

        case "Coach":
          navigate("/coach");
          break;

        case "Administrator": {
          const { data: programData } = await supabase
            .from("programs")
            .select("id")
            .eq("administrator", userId)
            .maybeSingle();

          if (!programData) {
            navigate("/admin");
            return;
          }

          const { data: teamData } = await supabase
            .from("teams")
            .select("id")
            .eq("program_id", programData.id)
            .limit(1)
            .maybeSingle();

          if (!teamData) {
            navigate("/admin");
          } else {
            navigate(
              `/admin/program/${programData.id}/team/${teamData.id}/dashboard`
            );
          }
          break;
        }
        case "SkillsTrainer": {
          const { data: programData } = await supabase
            .from("programs")
            .select("id")
            .eq("administrator", userId)
            .maybeSingle();

          if (!programData) {
            navigate("/skills-trainer/create-program");
            return;
          }

          navigate("/skills-trainer");
          break;
        }

        default:
          // Fallback if role is missing or unknown
          navigate("/");
          break;
      }
    } catch (err) {
      console.error("Redirection Error:", err);
      setError("Error determining user role.");
      setLoading(false); // Stop loading so they can try manually if needed
    }
  };

  // ---------------------------------------------------------
  // 2. AUTO-LOGIN CHECK (ON MOUNT)
  // ---------------------------------------------------------
  useEffect(() => {
    const checkSession = async () => {
      setLoading(true);
      const { data: { session } } = await supabase.auth.getSession();

      if (session?.user) {
        // User is already logged in (e.g., just created account or persisted session)
        // Immediately redirect them
        await handleRoleRedirect(session.user.id);
      } else {
        // No session, allow user to type in form
        setLoading(false);
      }
      setInitialCheckDone(true);
    };

    checkSession();
  }, []); // Empty dependency array = runs once on mount

  // ---------------------------------------------------------
  // 3. MANUAL LOGIN HANDLER
  // ---------------------------------------------------------
  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      // Authenticate
      const { data, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (authError) throw authError;

      if (data.user) {
        // Use the same helper function
        await handleRoleRedirect(data.user.id);
      }
    } catch (err) {
      console.error("Login Error:", err);
      setError(err.message || "Failed to sign in");
      setLoading(false);
    }
  };

  // Optional: Don't render the form until we've checked for a session
  // This prevents the login form from "flashing" briefly before redirecting
  if (!initialCheckDone) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
         {/* Simple Loading Spinner or Logo */}
         <div className="text-white animate-pulse">Loading NuvioSport...</div>
      </div>
    );
  }

  return (
    <div className="relative h-screen min-h-screen h-[100dvh] min-h-[100dvh] w-full overflow-hidden flex items-center justify-center font-sans bg-black">
      {/* --- BACKGROUND LAYER --- */}
      <div className="fixed inset-0 z-0">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1461896836934-ffe607ba8211?q=80&w=2070&auto=format&fit=crop')`,
          }}
        />
        <div className="absolute inset-0 bg-gray-900/40 backdrop-blur-[2px]"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-black/90"></div>
      </div>

      {/* --- LOGIN CARD --- */}
      <div
        className="relative z-10 w-full max-w-[420px] px-5 py-10"
        style={{
          transform:
            "translateY(calc(-0.5 * var(--install-prompt-height, 0px) + var(--login-center-offset, 0px)))",
        }}
      >
        <div className="bg-black/60 backdrop-blur-xl border border-white/10 p-7 md:p-10 rounded-3xl shadow-2xl overflow-y-auto max-h-[90svh]">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-black text-white tracking-tighter uppercase italic">
              Nuvio<span className="text-blue-500">Sport</span>
            </h1>
            <p className="text-blue-400 mt-1.5 text-[9px] font-black uppercase tracking-[0.4em] opacity-80">
              Performance Portal Access
            </p>
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/50 text-red-200 p-3 rounded-xl text-[11px] mb-6 text-center font-medium">
              {error}
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-5">
            <div className="space-y-1.5">
              <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">
                Account Email
              </label>
              <input
                type="email"
                placeholder="athlete@nuviosport.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-xl p-3.5 text-white placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-blue-500/50 transition-all text-sm"
                required
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">
                Password
              </label>
              <input
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-xl p-3.5 text-white placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-blue-500/50 transition-all text-sm"
                required
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold py-3.5 rounded-xl shadow-lg shadow-blue-900/20 transition-all active:scale-[0.98] disabled:opacity-50 uppercase tracking-widest text-[11px] mt-2"
            >
              {loading ? "Authenticating..." : "Sign In"}
            </button>

            <div className="flex flex-col space-y-4 items-center pt-6 border-t border-white/5 mt-6">
              <button
                type="button"
                className="text-gray-500 hover:text-white text-[10px] font-bold uppercase tracking-widest transition"
                onClick={() => navigate("/password-recovery")}
              >
                Forgot Password?
              </button>

              <p className="text-gray-500 text-[11px]">
                Not a member?{" "}
                <button
                  type="button"
                  className="text-blue-500 hover:text-blue-400 font-bold transition uppercase ml-1"
                  onClick={() => navigate("/create-account")}
                >
                  Create Account
                </button>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
